import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort, timeAgo, productImage } from "@/lib/api";
import { SectionTitle, Thumb, ClientStatusBadge } from "@/components/Bits";
import ApprovalActions from "@/components/ApprovalActions";

export default function ClientProductView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setProduct(data);
      const h = await api.get(`/products/${id}/approval-history`);
      setHistory(h.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!product) return <div className="py-24 text-center text-sm text-fog">Loading selection…</div>;

  const facts = [
    ["Dimensions", product.dimensions],
    ["Color / Finish", product.color_finish],
    ["Material", product.material],
    ["Lead Time", product.lead_time],
    ["Quantity", product.quantity],
  ].filter(([, v]) => v);

  return (
    <div className="space-y-10 pb-10" data-testid="client-product-view">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back
      </button>

      <div className="grid gap-10 lg:grid-cols-2">
        <div className="card-surface overflow-hidden">
          {productImage(product) ? (
            <img src={productImage(product)} alt={product.name} className="aspect-square w-full object-cover" />
          ) : (
            <Thumb className="aspect-square w-full" rounded="rounded-none" />
          )}
          {!!product.images?.length && (
            <div className="grid grid-cols-4 gap-2 p-4">
              {product.images.slice(0, 4).map((img, i) => (
                <img key={i} src={img} alt="" className="aspect-square w-full rounded-sm object-cover" />
              ))}
            </div>
          )}
        </div>

        <div className="space-y-8">
          <div>
            <div className="overline mb-3">{product.category}{product.space_name ? ` · ${product.space_name}` : ""}</div>
            <h1 data-testid="client-product-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{product.name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <ClientStatusBadge status={product.client_status} testId="client-product-status" />
              {product.client_total != null && <span className="num text-lg text-ink">{money(product.client_total)}</span>}
            </div>
          </div>

          {product.description && <p className="text-sm leading-relaxed text-stone">{product.description}</p>}

          {facts.length > 0 && (
            <div className="card-surface divide-y divide-line">
              {facts.map(([l, v]) => (
                <div key={l} className="flex justify-between gap-4 px-5 py-3 text-sm">
                  <span className="text-stone">{l}</span>
                  <span className="num text-ink">{v}</span>
                </div>
              ))}
            </div>
          )}

          {product.product_url && (
            <a href={product.product_url} target="_blank" rel="noreferrer" className="inline-block text-xs uppercase tracking-[0.15em] text-lime-dark hover:underline">
              View product page →
            </a>
          )}

          <ApprovalActions product={product} onChanged={load} />

          {product.presentation_id && (
            <Link to={`/presentations/${product.presentation_id}`} className="block text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
              See this in your design presentation →
            </Link>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <section className="max-w-2xl">
          <SectionTitle>Decision History</SectionTitle>
          <div className="card-surface divide-y divide-line" data-testid="client-approval-history">
            {history.map((h) => (
              <div key={h.id} className="px-5 py-4 text-sm">
                <div className="text-ink">
                  {h.action === "approved" ? "Approved" : "Alternative requested"} by {h.user_name}
                </div>
                <div className="mt-1 text-xs text-fog">
                  {dateShort(h.created_at)} · {timeAgo(h.created_at)}
                  {h.price_at_approval != null && ` · ${money(h.price_at_approval)}`}
                  {h.quantity_at_approval ? ` · qty ${h.quantity_at_approval}` : ""}
                </div>
                {h.reasons?.length ? <div className="mt-1 text-xs text-stone">{h.reasons.join(", ")}</div> : null}
                {h.comment ? <div className="mt-1 text-xs text-stone">“{h.comment}”</div> : null}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
